import {
  InvalidUser,
  UserImportErrorTypeUser
} from '@dadohr/shared/domainTypes/user';
import { useSort } from '../../hooks/useSort';
import { useUnsafeTranslation } from '../../i18next';
import { LightMdToJsx } from '../../services/formatter';
import { toFullName } from '../../services/user';
import {
  ItemSorter,
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS
} from '../GroupableList';
import { IColumn } from '../Table/VirtualizedSortable';

type ColumnName = 'name' | 'email' | 'issue';
type D = InvalidUser;

type Column = IColumn<D, ColumnName>;

const ROW_TO_KEY = (d: D) => d.user.id;

// Map raw per-user commit error strings to something admins can act on.
// The raw text comes straight from `(err as Error).message` inside
// mutateUsersBatch — usually a Firestore SDK error or a validateIntent
// rejection. We pattern-match the known shapes and fall back to a generic
// summary. The synthetic 'COMMIT_REJECTED' issue (used by PARTIAL_FAILURE
// imports) routes through this branch.
const friendlyCommitError = (raw: string): string => {
  if (!raw) return 'This user could not be saved.';
  const undefinedMatch = raw.match(
    /Cannot use "undefined" as a Firestore value[^]*?(?:found in field "([^"]+)")?/i
  );
  if (undefinedMatch) {
    const field = undefinedMatch[1];
    if (field === 'createdAt' || field === 'updatedAt') {
      return `An internal field ("${field}") could not be saved. This was caused by a recently-fixed bug in import diffing — re-running the import should resolve it.`;
    }
    return field
      ? `The "${field}" field was missing or empty in the import. Add a value in the source and re-import.`
      : 'A field was missing or empty in the import. Check the source row and re-import.';
  }
  const validationMatch = raw.match(/validation rejected:\s*(.+)$/i);
  if (validationMatch) {
    return validationMatch[1].trim();
  }
  if (/permission[- ]?denied/i.test(raw)) {
    return 'Permission was denied while saving this user. Verify the import service has write access to the org.';
  }
  return 'This user could not be saved. See technical details below.';
};

const IssueDescription = ({ issueData }: { issueData: InvalidUser }) => {
  const { t } = useUnsafeTranslation();
  // Synthetic issue created by Details.tsx for PARTIAL_FAILURE imports —
  // each commit-time rejection from mutateUsersBatch arrives here as an
  // InvalidUser with issue='COMMIT_REJECTED' and errorData.value=raw
  // error string. The enum value isn't formal yet (lands in shared next
  // bump), so we compare against the string literal.
  if ((issueData.issue as string) === 'COMMIT_REJECTED') {
    const raw = issueData.errorData?.value ?? '';
    const friendly = friendlyCommitError(raw);
    return (
      <div>
        <div>{friendly}</div>
        {raw && raw !== friendly && (
          <details style={{ marginTop: 4 }}>
            <summary style={{ cursor: 'pointer', fontSize: '0.85em' }}>
              {t('Show technical details')}
            </summary>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.8em',
                margin: '4px 0 0 0',
                opacity: 0.75
              }}
            >
              {raw}
            </pre>
          </details>
        )}
      </div>
    );
  }
  if (issueData.issue === UserImportErrorTypeUser.NO_EMAIL_IDENTIFIER) {
    return <div>{t('Neither work nor personal email address present.')}</div>;
  }
  // This is obsolete and will not arise in the future anymore
  if (issueData.issue === UserImportErrorTypeUser.NO_START_DATE) {
    return <div>{t('No first day at work present.')}</div>;
  }
  if (issueData.issue === UserImportErrorTypeUser.INVALID_EMAIL_ADDRESS) {
    if (issueData.errorData) {
      return (
        <LightMdToJsx>
          {t('import.errors.row.invalidEmailAddress', {
            emailType:
              issueData.errorData.field === 'email' ? t('work') : t('personal'),
            email: issueData.errorData.value
          })}
        </LightMdToJsx>
      );
    } else {
      return <div>{t('import.errors.row.invalidEmailAddressFallback')}</div>;
    }
  }
  if (issueData.issue === UserImportErrorTypeUser.DUPLICATE_EMAIL_ADDRESS) {
    if (issueData.errorData) {
      return (
        <div>
          {t('import.errors.row.duplicateEmailAddress', {
            emailType:
              issueData.errorData.field === 'email' ? t('work') : t('personal'),
            email: issueData.errorData.value
          })}
        </div>
      );
    } else {
      return <div>{t('import.errors.row.duplicateEmailAddressFallback')}</div>;
    }
  }

  if (issueData.issue === UserImportErrorTypeUser.NO_WORK_EMAIL) {
    return <div>{t('Missing email.')}</div>;
  }
  if (issueData.issue === UserImportErrorTypeUser.INVALID_EMAIL_DOMAIN) {
    return <div>{t('Invalid email domain.')}</div>;
  }

  if (issueData.issue === UserImportErrorTypeUser.NO_FIRST_NAME) {
    return <div>{t('Missing first name.')}</div>;
  }

  if (issueData.issue === UserImportErrorTypeUser.NO_LAST_NAME) {
    return <div>{t('Missing last name.')}</div>;
  }

  console.warn('UNKNOWN_ISSUE', issueData.issue);
  return null;
};

const COLUMNS: Column[] = [
  {
    key: 'name',
    head: () => 'Name',
    cell: (d) => {
      return toFullName(d.user.data);
    },
    align: 'left',
    sortable: true,
    width: 120,
    flexGrow: 1
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => {
      return d.user.data.email || d.user.data.personalEmail;
    },
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 1
  },
  {
    key: 'issue',
    head: () => 'Issue',
    cell: (d) => <IssueDescription issueData={d} />,
    align: 'left',
    sortable: true,
    width: 200,
    flexGrow: 3
  }
];

const SORTERS: ItemSorters<D> = {
  name: {
    key: 'name',
    items: {
      sort: (d) => toFullName(d.user.data),
      dir: 'asc'
    }
  },
  email: {
    key: 'email',
    items: {
      sort: (d) => d.user.data.email || d.user.data.personalEmail,
      dir: 'asc'
    }
  },
  issue: {
    key: 'issue',
    items: { sort: (d) => d.issue, dir: 'asc' }
  }
};

export const UserIssuesTable = ({
  ds,
  variant = 'contained'
}: {
  ds: InvalidUser[];
  variant?: 'contained' | 'plain';
}) => {
  const { sortBy, direction, setSort } = useSort<ItemSorter<D>>(
    SORTERS.name,
    SORTERS.name.items.dir
  );
  return (
    <RowsRenderer
      variant={variant}
      columns={COLUMNS}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={undefined}
    />
  );
};
